/**
 * ConnectivityWizard.tsx — the ONE connection wizard (replaces the old
 * ApiConnectModal + the standalone VskiSetupModal).
 *
 * Five steps, walked in order; each opens with its exit condition derived
 * from live probes so an already-configured stack lands straight on Done:
 *
 *   1 Services  — the Docker stack. No backend at the target origin → the
 *                 run-it-yourself instructions (download compose + .env with
 *                 the data dir baked in, docker compose up). Next re-probes.
 *   2 Storage   — the VSKI platform behind the backend. Not reachable →
 *                 custom address probe; reachable but no admin → open the
 *                 platform's /installer.
 *   3 Login     — platform superuser email/password (the backend mints +
 *                 stores an admin key — shared VskiCredentialForm) or a
 *                 pasted key. Auto-satisfied when already configured.
 *   4 API       — this client's WS connection (default ws://localhost:8000/ws),
 *                 optional API key, relay toggle, live Test.
 *   5 Done      — checkmark + a summary row per step with a Change link that
 *                 jumps back (the "return to change any params" surface).
 *
 * The wizard is SELF-SUFFICIENT: it probes its own draft origin (the WS URL
 * field) for /health + /api/vski/status, independent of the app-wide
 * useConnectivity poll (which only knows the COMMITTED auth.wsUrl). The
 * connection is committed exactly once — step 4's Connect writes AuthContext;
 * everything else (library adapters, ApiAdapter) reacts to that as before.
 *
 * `embedded` renders it as the start page's body (shown while disconnected);
 * otherwise it lives inside ConnectivityModal.
 */
import { useEffect, useState } from 'react';
import { CheckCircle, DownloadSimple, WarningCircle } from '@phosphor-icons/react';
import composeYml from '../../docker-compose.release.yml?raw';

import { useAuth } from '../auth/AuthContext';

import { VskiCredentialForm } from '../panels/VskiCredentialForm';
import { useVskiStatus } from '../vski/vskiConfig';
import {
  apiStepDone,
  initialWizardStep,
  loginStepDone,
  servicesStepDone,
  storageStepDone,
  type ConnectivitySnapshot,
  type WizardStep,
} from './connectivity';
import { DEFAULT_WS_URL, wsUrlToOrigin } from './httpOrigin';
import { testWsConnection, type WsTestResult } from './testWs';
import { useBackendHealth, useConnectivity } from './useConnectivity';

const STEPS: { id: WizardStep; label: string }[] = [
  { id: 'services', label: 'Services' },
  { id: 'storage', label: 'Storage' },
  { id: 'login', label: 'Login' },
  { id: 'api', label: 'Setup API' },
  { id: 'done', label: 'Review' },
];

/** The .env offered for download — a curated template (not the repo's full
 * .env.example) with the user's data dir baked in. */
const envTemplate = (dataDir: string): string => `# vski-distillery local stack
# Where project data lives on the host (absolute path recommended).
VSKI_DATA_DIR=${dataDir.trim() || './vski-data'}
# Host ports (change if occupied).
API_PORT=8000
VSKI_PORT=3000
# Container user for bind-mounted data dirs — set to your uid:gid to avoid
# permission issues on the mounted files.
VSKI_UID=1000
VSKI_GID=1000
`;

/** Client-side text download (same pattern as the Composer's JSON export). */
function downloadText(name: string, text: string): void {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

interface ConnectivityWizardProps {
  /** Embedded = rendered as the start page's body (no modal chrome). */
  embedded?: boolean;
  /** Close handler (modal mode — the Done button on the summary step). */
  onClose?: () => void;
}

export function ConnectivityWizard({
  embedded = false,
  onClose,
}: ConnectivityWizardProps): JSX.Element {
  const auth = useAuth();
  const connectivity = useConnectivity();
  const [step, setStep] = useState<WizardStep | null>(null);
  const [wsUrlDraft, setWsUrlDraft] = useState(auth.wsUrl ?? DEFAULT_WS_URL);
  const [apiKeyDraft, setApiKeyDraft] = useState(auth.token ?? '');
  const [probeUrl, setProbeUrl] = useState<string | null>(null);
  const [customUrl, setCustomUrl] = useState('');
  const [dataDir, setDataDir] = useState('./vski-data');
  const [wsTesting, setWsTesting] = useState(false);
  const [wsTest, setWsTest] = useState<WsTestResult | null>(null);
  const origin = wsUrlToOrigin(wsUrlDraft.trim());
  const health = useBackendHealth(origin);
  const vski = useVskiStatus(origin, auth.token, probeUrl);
  const status = vski.status;

  const snap: ConnectivitySnapshot = {
    wsUrl: auth.wsUrl,
    backendOk: health.ok,
    vski: status,
  };

  // Open at the first unsatisfied step — but only once BOTH probes settle,
  // so a configured stack lands on Done instead of flashing through Services.
  useEffect(() => {
    if (step === null && health.ok !== null && !vski.loading) {
      setStep(initialWizardStep(snap));
    }
  });

  const goto = (next: WizardStep): void => {
    setStep(next);
    setWsTest(null);
  };

  const stepDone = (id: WizardStep): boolean => {
    switch (id) {
      case 'services':
        return servicesStepDone(snap);
      case 'storage':
        return storageStepDone(snap);
      case 'login':
        return loginStepDone(snap);
      case 'api':
        return apiStepDone(snap);
      case 'done':
        return true;
    }
  };

  // Steps beyond Services need a backend to probe against.
  const canGo = (id: WizardStep): boolean => id === 'services' || id === 'done' || health.ok === true;

  const platformUrlForMutation: string | undefined =
    (probeUrl || status?.suggestedUrl || status?.url || customUrl || '').trim() || undefined;

  const runTest = (): void => {
    setWsTesting(true);
    setWsTest(null);
    testWsConnection(wsUrlDraft.trim(), apiKeyDraft.trim(), true)
      .then((r) => setWsTest(r))
      .finally(() => setWsTesting(false));
  };

  /** Commit the connection — the ONE place the wizard writes AuthContext. */
  const commitConnection = (): void => {
    const key = apiKeyDraft.trim();
    auth.setToken(key ? key : null);
    auth.setWsConnection(wsUrlDraft.trim(), true);
    connectivity.reload();
    goto('done');
  };

  const checkCustomUrl = (): void => {
    const trimmed = customUrl.trim();
    if (!trimmed) return;
    setProbeUrl(trimmed.replace(/\/+$/, ''));
  };

  // ─── render ───────────────────────────────────────────────────────────—

  if (step === null) {
    return (
      <div className="conn-wizard">
        <p className="api-connect-hint">Checking connectivity…</p>
      </div>
    );
  }

  const healthStatus =
    health.ok === null ? (
      <div className="api-connect-status test-testing" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--warn)' }}>●</span> checking…
      </div>
    ) : health.ok ? (
      <div className="api-connect-status test-ok" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--good)' }}>●</span> Ready
      </div>
    ) : (
      <div className="api-connect-status test-fail" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--warn)' }}>●</span> Waiting for Docker
      </div>
    );

  let storageStatus = null;
  if (status && !status.sdk) {
    storageStatus = (
      <div className="api-connect-status test-fail" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--warn)' }}>●</span> Backend is missing vski-sdk
      </div>
    );
  } else if (vski.loading && !status) {
    storageStatus = (
      <div className="api-connect-status test-testing" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--warn)' }}>●</span> checking…
      </div>
    );
  } else if (vski.error && !status) {
    storageStatus = (
      <div className="api-connect-status test-fail" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--warn)' }}>●</span> {vski.error}
        <button type="button" className="btn btn-mini" onClick={vski.reload}>Retry</button>
      </div>
    );
  } else if (status && status.configured) {
    storageStatus = (
      <div className="api-connect-status test-ok" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--good)' }}>●</span> Ready
      </div>
    );
  } else if (status && !status.configured && !status.platform.reachable) {
    storageStatus = (
      <div className="api-connect-status test-fail" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--warn)' }}>●</span> {status.platform.detail || 'No platform found'}
      </div>
    );
  } else if (status && !status.configured && status.platform.reachable && !status.platform.hasAdmins) {
    storageStatus = (
      <div className="api-connect-status test-testing" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--warn)' }}>●</span> waiting for admin creation
      </div>
    );
  } else if (status && !status.configured && status.platform.reachable && status.platform.hasAdmins) {
    storageStatus = (
      <div className="api-connect-status test-ok" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--good)' }}>●</span> Ready
      </div>
    );
  }

  let loginStatus = null;
  if (status && status.configured && status.platform.authOk === false) {
    loginStatus = (
      <div className="api-connect-status test-fail" style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
        <span style={{ color: 'var(--warn)' }}>●</span> Key rejected
      </div>
    );
  }

  return (
    <div className={`conn-wizard${embedded ? ' conn-wizard-embedded' : ''}`}>
      <ol className="conn-steps">
        {STEPS.map((s, i) => (
          <li key={s.id}>
            <button
              type="button"
              className={`conn-step${step === s.id ? ' active' : ''}${stepDone(s.id) ? ' done' : ''}`}
              disabled={!canGo(s.id)}
              onClick={() => goto(s.id)}
            >
              <span className="conn-step-num">{s.id === 'done' ? '✓' : i + 1}</span>
              {s.label}
            </button>
          </li>
        ))}
      </ol>

      {/* ─── 1 · Services ──────────────────────────────────────────────── */}
      {step === 'services' && (
        <div className="conn-section">
          <h3 className="conn-title">Docker services</h3>
          <label className="field">
            <span className="field-label">Backend WebSocket URL</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                className="text-input"
                value={wsUrlDraft}
                onChange={(e) => {
                  setWsUrlDraft(e.target.value);
                  setProbeUrl(null);
                }}
                placeholder={DEFAULT_WS_URL}
                spellCheck={false}
              />
              <button
                type="button"
                className="btn"
                onClick={() => {
                  health.recheck();
                  vski.reload();
                }}
              >
                Check
              </button>
            </div>
          </label>
          {health.ok === false && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
            <details className="conn-accordion" open>
              <summary>Setup with vcli (recommended)</summary>
              <div>
                <p className="api-connect-hint" style={{ marginTop: '0' }}>
                  Downloads the vcli binary from the platform releases, then scaffolds the lab
                  layout (docker-compose.yml, docker/, data/, projects/, .env) in the current
                  directory and brings the stack up.
                </p>
                <pre className="conn-cmd">{'curl -fsSL https://vski.sh/x/platform/releases/download/latest/vcli -o vcli && chmod +x vcli'}</pre>
                <pre className="conn-cmd">{'./vcli lab init --origin ' + window.location.origin}</pre>
              </div>
            </details>
            <details className="conn-accordion">
              <summary>Bash script for auto setup</summary>
              <div>
                <p className="api-connect-hint" style={{ marginTop: '0' }}>Downloads docker compose file and env in current directory.</p>
                <pre className="conn-cmd">curl -fsSL {window.location.origin}/install.sh | bash -s -- {window.location.origin}</pre>
              </div>
            </details>
            <details className="conn-accordion">
              <summary>Manual setup</summary>
              <div className="conn-howto" style={{ marginTop: '8px', border: 'none', padding: 0, background: 'transparent' }}>
                <ol className="conn-howto-list">
                  <li>
                    <strong>Get the services.</strong> From the app's repository, place the two
                    files below in the repo root (or keep the copies that ship with the source):
                    <div className="api-connect-actions conn-howto-left">
                      <button
                        type="button"
                        className="btn"
                        onClick={() => downloadText('docker-compose.yml', composeYml)}
                      >
                        <DownloadSimple size={14} /> docker-compose.yml
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => downloadText('.env', envTemplate(dataDir))}
                      >
                        <DownloadSimple size={14} /> .env
                      </button>
                    </div>
                  </li>
                  <li>
                    <strong>Configure the data dir.</strong> Where project data lives on the
                    host — baked into the downloaded <code>.env</code> as{' '}
                    <code>VSKI_DATA_DIR</code>:
                    <label className="field">
                      <span className="field-label">Data directory</span>
                      <input
                        type="text"
                        className="text-input"
                        value={dataDir}
                        onChange={(e) => setDataDir(e.target.value)}
                        placeholder="/srv/vski-distillery-data"
                        spellCheck={false}
                      />
                    </label>
                  </li>
                  <li>
                    <strong>Run.</strong> The API comes up on <code>:8000</code>, the platform on{' '}
                    <code>:3000</code>; this page re-checks automatically.
                    <pre className="conn-cmd">docker compose up -d</pre>
                  </li>
                </ol>
              </div>
            </details>
          </div>
          )}
          <div className="conn-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {healthStatus}
            <button
              type="button"
              className="btn btn-start"
              disabled={health.ok !== true}
              onClick={() => goto('storage')}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ─── 2 · Storage (the platform data store / installer) ─────────── */}
      {step === 'storage' && (
        <div className="conn-section">
          <h3 className="conn-title">Platform storage</h3>
          {status && (
            <>
              {status.configured && (
                <div className="vski-setup-card vski-ok">
                  <div className="vski-setup-title">✓ Storage: VSKI Platform</div>
                  <div className="vski-setup-meta">
                    <code>{status.url}</code>
                    <span className="vski-setup-source">
                      key: {status.source === 'env' ? 'env var (operator-managed)' : 'stored (encrypted)'}
                    </span>
                  </div>
                </div>
              )}
              {!status.configured && !status.platform.reachable && (
                <div className="vski-setup-card">
                  <div className="vski-setup-title">No platform found</div>
                  <p className="api-connect-hint">
                    The backend found no platform at the conventional addresses. It starts with the stack (docker compose up -d vski) — or enter its address:
                  </p>
                  <label className="field">
                    <span className="field-label">Platform URL</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        className="text-input"
                        value={customUrl}
                        onChange={(e) => setCustomUrl(e.target.value)}
                        placeholder="http://localhost:3000"
                        spellCheck={false}
                      />
                      <button
                        type="button"
                        className="btn"
                        onClick={checkCustomUrl}
                        disabled={!customUrl.trim()}
                      >
                        Check
                      </button>
                      <button type="button" className="btn" onClick={vski.reload}>
                        Retry
                      </button>
                    </div>
                  </label>
                </div>
              )}
              {!status.configured &&
                status.platform.reachable &&
                !status.platform.hasAdmins && (
                  <div className="vski-setup-card">
                    <div className="vski-setup-title">Create the platform administrator</div>
                    <p className="api-connect-hint">
                      The platform has no superuser yet. Open its first-run installer, choose
                      an email + password, then come back — the login step uses those
                      credentials once and never stores the password.
                    </p>
                    {status.installerUrl && (
                      <div className="api-connect-actions conn-howto-left">
                        <a
                          className="btn btn-start"
                          href={status.installerUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open the installer ↗
                        </a>
                        <button type="button" className="btn" onClick={vski.reload}>
                          I created the admin — recheck
                        </button>
                      </div>
                    )}
                  </div>
                )}
              {!status.configured &&
                status.platform.reachable &&
                status.platform.hasAdmins && (
                  <div className="vski-setup-card vski-ok">
                    <div className="vski-setup-title">✓ Platform ready</div>
                    <p className="api-connect-hint">
                      An administrator exists — the next step connects the platform.
                    </p>
                  </div>
                )}
            </>
          )}
          <div className="conn-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {storageStatus || <div />}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn" onClick={() => goto('services')}>
                Back
              </button>
              <button
                type="button"
                className="btn btn-start"
                disabled={!storageStepDone(snap)}
                onClick={() => goto('login')}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── 3 · Login (mint + store the platform key) ─────────────────── */}
      {step === 'login' && (
        <div className="conn-section">
          <h3 className="conn-title">Platform login</h3>
          {status && status.configured && status.platform.authOk !== false && (
            <div className="vski-setup-card vski-ok">
              <div className="vski-setup-title">✓ Storage connected</div>
              <div className="vski-setup-meta">
                <code>{status.url}</code>
                <span className="vski-setup-source">
                  key: {status.source === 'env' ? 'env var (operator-managed)' : 'stored (encrypted)'}
                </span>
              </div>
            </div>
          )}
          {status && !status.configured && (
            <p className="api-connect-hint">
              Log in with the platform superuser. The backend mints an admin API key and
              stores it encrypted — the password is used once and never persisted.
            </p>
          )}
          {(!status || !status.configured || status.platform.authOk === false) && (
            <div className="vski-setup-card">
              <VskiCredentialForm
                backendOrigin={origin}
                token={auth.token}
                platformUrl={platformUrlForMutation}
                keystore={status?.keystore ?? true}
                onChanged={vski.reload}
              />
            </div>
          )}
          <div className="conn-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {loginStatus || <div />}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn" onClick={() => goto('storage')}>
                Back
              </button>
              <button
                type="button"
                className="btn btn-start"
                disabled={!loginStepDone(snap)}
                onClick={() => goto('api')}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── 4 · Distillery API (this client's connection) ─────────────── */}
      {step === 'api' && (
        <div className="conn-section">
          <h3 className="conn-title">Distillery API</h3>
          {auth.wsUrl && (
            <p className="api-connect-hint">
              Currently connected to <code>{auth.wsUrl}</code>. Adjust and press Connect to re-apply.
            </p>
          )}
          <label className="field">
            <span className="field-label">WebSocket URL</span>
            <input
              type="text"
              className="text-input"
              value={wsUrlDraft}
              onChange={(e) => setWsUrlDraft(e.target.value)}
              placeholder={DEFAULT_WS_URL}
              autoFocus
              spellCheck={false}
            />
          </label>
          <label className="field">
            <span className="field-label">API key</span>
            <input
              type="text"
              className="text-input"
              value={apiKeyDraft}
              onChange={(e) => setApiKeyDraft(e.target.value)}
              placeholder="(optional — leave blank if no auth)"
              spellCheck={false}
            />
          </label>
          <div className="conn-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {wsTest ? (
              <div className={`api-connect-status test-${wsTest.ok ? 'ok' : 'fail'}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                <span style={{ color: wsTest.ok ? 'var(--good)' : 'var(--warn)' }}>●</span> {wsTest.ok ? wsTest.label : wsTest.message}
              </div>
            ) : <div />}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn" onClick={() => goto('login')}>
                Back
              </button>
              <button
                type="button"
                className="btn"
                onClick={runTest}
                disabled={wsTesting || !wsUrlDraft.trim()}
              >
                {wsTesting ? 'Testing…' : 'Test'}
              </button>
              <button
                type="button"
                className="btn btn-start"
                onClick={commitConnection}
                disabled={!wsUrlDraft.trim()}
              >
                Connect
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── 5 · Done (summary + per-step Change links) ────────────────── */}
      {step === 'done' && (
        <div className="conn-section conn-done">
          {connectivity.derived.connected ? (
            <>
              <CheckCircle size={36} weight="fill" className="conn-done-icon ok" />
              <h3 className="conn-title">Everything is connected</h3>
            </>
          ) : (
            <>
              <WarningCircle size={36} weight="fill" className="conn-done-icon bad" />
              <h3 className="conn-title">Not connected yet</h3>
            </>
          )}
          <ul className="conn-summary">
            <li>
              <span className="conn-summary-label">Services</span>
              <span className="conn-summary-value">
                <code>{origin ?? wsUrlDraft}</code>
                {health.ok === true ? ' · healthy' : health.ok === false ? ' · unreachable' : ' · checking…'}
              </span>
              <button type="button" className="btn-link" onClick={() => goto('services')}>
                Change
              </button>
            </li>
            <li>
              <span className="conn-summary-label">Storage</span>
              <span className="conn-summary-value">
                {status?.configured
                  ? (<><code>{status.url}</code> · key: {status.source === 'env' ? 'env var' : 'stored'}</>)
                  : 'Not configured'}
              </span>
              {status?.configured ? (
                <button
                  type="button"
                  className="btn-link"
                  onClick={() => goto('storage')}
                >
                  Change
                </button>
              ) : (
                <button type="button" className="btn-link" onClick={() => goto('storage')}>
                  Set up
                </button>
              )}
            </li>
            <li>
              <span className="conn-summary-label">API</span>
              <span className="conn-summary-value">
                {auth.wsUrl ? (<><code>{auth.wsUrl}</code></>) : 'Not connected'}
              </span>
              <button type="button" className="btn-link" onClick={() => goto('api')}>
                Change
              </button>
            </li>
          </ul>
          <div className="conn-actions">
            <button
              type="button"
              className="btn btn-start"
              onClick={() => (embedded ? connectivity.reload() : onClose?.())}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
